// ────────────────────────────────────────────────────────────────
//  Canonical board connectivity status
//
//  THE single source of truth for whether a board is online. Every page
//  (จัดการอุปกรณ์, รายละเอียดบอร์ด, จัดการไซต์, แดชบอร์ด) must derive its
//  online/offline badge from here so a board never reads Online on one screen
//  and Offline on another.
//
//  Decision order:
//    1. Broker-authoritative MQTT presence (EMQX keepalive, ~45 s) — wins when
//       known. `true` → online, `false` → offline.
//    2. Fallback to telemetry freshness ONLY until the first presence event:
//         ≤ 2 min since last activity → online
//         2–5 min                     → stale
//         > 5 min                     → offline
//         never reported              → never
// ────────────────────────────────────────────────────────────────

export type BoardStatus = 'online' | 'stale' | 'offline' | 'never';

/** Telemetry-freshness fallback windows (ms). Used only until the broker
 *  reports presence. Kept here so every page shares the exact thresholds. */
export const BOARD_ONLINE_MS = 120_000; // ≤ 2 min → online
export const BOARD_STALE_MS = 300_000; //  2–5 min → stale, older → offline

export interface BoardStatusInput {
  /** Broker-authoritative MQTT presence. Preferred signal when defined. */
  brokerConnected?: boolean | null;
  /** Most recent activity — ISO string or epoch ms. Callers with realtime
   *  telemetry should pass max(board.lastSeenAt, newest telemetry point). */
  lastActivity?: string | number | null;
}

function toMs(v: string | number | null | undefined): number | null {
  if (v == null) return null;
  const ms = typeof v === 'number' ? v : new Date(v).getTime();
  return Number.isNaN(ms) ? null : ms;
}

/** THE board status function — pass broker presence + most-recent activity. */
export function getBoardStatus(
  input: BoardStatusInput,
  now: number = Date.now(),
): BoardStatus {
  if (input.brokerConnected === true) return 'online';
  if (input.brokerConnected === false) return 'offline';
  const ms = toMs(input.lastActivity);
  if (ms == null) return 'never';
  const age = now - ms;
  if (age < BOARD_ONLINE_MS) return 'online';
  if (age < BOARD_STALE_MS) return 'stale';
  return 'offline';
}

/** Convenience over a board record. `extraActivityMs` lets realtime pages fold
 *  in the newest in-memory telemetry point (fresher than the stored lastSeenAt). */
export function boardStatusOf(
  board: { brokerConnected?: boolean | null; lastSeenAt?: string | null },
  extraActivityMs?: number | null,
  now?: number,
): BoardStatus {
  const base = toMs(board.lastSeenAt);
  const lastActivity =
    extraActivityMs != null ? Math.max(base ?? 0, extraActivityMs) : base;
  return getBoardStatus(
    { brokerConnected: board.brokerConnected, lastActivity },
    now,
  );
}

/** Shorthand for the many KPI/count call-sites. Stale counts as NOT online. */
export function isBoardOnline(
  board: { brokerConnected?: boolean | null; lastSeenAt?: string | null },
  extraActivityMs?: number | null,
  now?: number,
): boolean {
  return boardStatusOf(board, extraActivityMs, now) === 'online';
}

export type MqttState = 'connected' | 'disconnected' | 'unknown';

/** THE single source of truth for a board's MQTT link state — used by every
 *  page + the diagnosis popup so they can never contradict each other.
 *
 *  Decision order:
 *    1. Broker-authoritative presence (EMQX keepalive) — wins when known.
 *    2. Otherwise, FRESH telemetry (< 2 min) proves the link was up when the
 *       board published, so treat it as connected.
 *    3. Otherwise we genuinely don't know → 'unknown'. (Never fall back to a
 *       board's stale self-reported mqttUp — that's what made the detail page
 *       claim "connected" from a 40-min-old report while the index said "หลุด".)
 */
export function mqttDisplayState(
  brokerConnected: boolean | null | undefined,
  lastActivityMs: number | null | undefined,
  now: number = Date.now(),
): MqttState {
  if (brokerConnected === true) return 'connected';
  if (brokerConnected === false) return 'disconnected';
  if (lastActivityMs != null && now - lastActivityMs < BOARD_ONLINE_MS) return 'connected';
  return 'unknown';
}

export const MQTT_STATE_META: Record<MqttState, { color: string; label: string }> = {
  connected: { color: '#22c55e', label: 'เชื่อมต่อ' },
  disconnected: { color: '#ef4444', label: 'หลุด' },
  unknown: { color: '#94a3b8', label: 'ไม่ทราบ' },
};

/** Shared colour + Thai label per status, so every badge across every page
 *  shows the exact same word and hue for the same state. */
export const BOARD_STATUS_META: Record<
  BoardStatus,
  { color: string; label: string }
> = {
  online: { color: 'var(--green)', label: 'ออนไลน์' },
  stale: { color: 'var(--yellow)', label: 'หน่วง' },
  offline: { color: 'var(--red)', label: 'ออฟไลน์' },
  never: { color: 'var(--dim)', label: 'ไม่มีข้อมูล' },
};
